import type { Grade, GradeScale } from '../types/entities'

/**
 * Единая математика по оценкам для web (журнал, аналитика, экспорт).
 *
 * Формулы совпадают с KMP-клиентом (`app/shared/.../core/domain/GradeStats.kt`
 * и `GradeValue.toNumericForAverage`), чтобы web и desktop/Android показывали
 * одинаковый средний балл по одним и тем же данным:
 *  - средневзвешенный балл = Σ(балл · вес) / Σ(вес);
 *  - 100-балльная шкала переводится в 5-балльную: n / 20, ограничение [2; 5];
 *  - буквенная: A=5, B=4, C=3, D=2, F=1 (модификаторы «+/-» игнорируются);
 *  - зачёт/незачёт: 5 / 2;
 *  - «н» (не был) и нечисловые значения в среднее не входят.
 */

type GradeLike = Pick<Grade, 'value' | 'scale'> & { weight?: number | null }

const LETTERS: Record<string, number> = { A: 5, B: 4, C: 3, D: 2, F: 1 }
const PASS = new Set(['зач', 'зачёт', 'зачет', 'зч', 'pass', '+'])
const FAIL = new Set(['нзч', 'незачёт', 'незачет', 'fail', '-'])

/** Численный эквивалент оценки для расчёта среднего; null — не участвует. */
export function gradeNumericValue(value: string, scale: GradeScale | string | undefined): number | null {
  const v = (value ?? '').trim()
  if (!v) return null
  switch (scale) {
    case 'passFail': {
      const low = v.toLowerCase()
      if (PASS.has(low)) return 5
      if (FAIL.has(low)) return 2
      return null
    }
    case 'letter':
      return LETTERS[v.charAt(0).toUpperCase()] ?? null
    case 'hundredPoint': {
      const n = Number.parseFloat(v)
      if (!Number.isFinite(n)) return null
      return Math.min(5, Math.max(2, n / 20))
    }
    default: {
      // fivePoint / tenPoint / неизвестная шкала: «4+» → 4, «н» → null
      const n = Number.parseFloat(v)
      return Number.isFinite(n) ? n : null
    }
  }
}

function effectiveWeight(w: number | null | undefined): number {
  return typeof w === 'number' && Number.isFinite(w) && w >= 0 ? w : 1
}

/** Средневзвешенный балл Σ(n·w)/Σw; null, если численных оценок нет или Σw = 0. */
export function weightedAverage(grades: Iterable<GradeLike>): number | null {
  let sum = 0
  let totalW = 0
  let count = 0
  for (const g of grades) {
    const n = gradeNumericValue(g.value, g.scale)
    if (n == null) continue
    const w = effectiveWeight(g.weight)
    sum += n * w
    totalW += w
    count++
  }
  if (count === 0 || totalW <= 0) return null
  return sum / totalW
}

/**
 * «Качество знаний» — доля оценок «4» и «5» (в процентах, округлено) среди
 * оценок с численным эквивалентом. Считается по исходным баллам, без весов:
 * «2» с весом 2 остаётся двойкой.
 */
export function qualityPct(grades: Iterable<GradeLike>): number | null {
  let total = 0
  let good = 0
  for (const g of grades) {
    const n = gradeNumericValue(g.value, g.scale)
    if (n == null) continue
    total++
    if (n >= 4) good++
  }
  return total > 0 ? Math.round((good / total) * 100) : null
}
